import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import { RiskMerchValue } from "@/lib/api/risk/merchValues";
import {
  MerchValueFormValues,
  MerchValueValidator,
  MERCH_VALUE_TYPE,
} from "./form-schema";
import { convertFormatMomentToLocalTimezone } from "@/lib/api/date";

// ---------------------------------------------------------------------------
// API → Form
// ---------------------------------------------------------------------------

/**
 * Convert an API RiskMerchValue record into the shape the form uses.
 *
 * Dates come back as ISO UTC strings from the API and are stored in
 * America/Chicago — parse them in that timezone so the date picker
 * shows the correct calendar day.
 */
export function apiToForm(record: RiskMerchValue): MerchValueFormValues {
  return {
    merch_value_type: MERCH_VALUE_TYPE,
    market_zone_id: record.market_zone_id,
    commodity: record.commodity,
    crop_year: record.crop_year,
    note: record.note ?? undefined,
    value: record.value ?? undefined,
    range_start: convertFormatMomentToLocalTimezone(moment.tz(record.range_start, CommonDestinationTimezone)),
    range_end: convertFormatMomentToLocalTimezone(moment.tz(record.range_end, CommonDestinationTimezone)),
  };
}

// ---------------------------------------------------------------------------
// Form → Create API payload
// ---------------------------------------------------------------------------

export function formToCreateApi(form: MerchValueFormValues): {
  market_zone_id: string;
  commodity: string;
  crop_year: number;
  note: string | null;
  value: string | null;
  range_start: string;
  range_end: string;
} {
  const serialized: Record<string, unknown> = {
    merch_value_type: MERCH_VALUE_TYPE,
    market_zone_id: form.market_zone_id,
    commodity: form.commodity,
    crop_year: form.crop_year,
    note: form.note || null,
    value: form.value || null,
    range_start: form.range_start instanceof Date ? moment(form.range_start).format("YYYY-MM-DD") : undefined,
    range_end: form.range_end instanceof Date ? moment(form.range_end).format("YYYY-MM-DD") : undefined,
  };

  const parsed = MerchValueValidator.parse(serialized);

  return {
    market_zone_id: parsed.market_zone_id,
    commodity: parsed.commodity,
    crop_year: parsed.crop_year,
    note: parsed.note ?? null,
    value: parsed.value ?? null,
    range_start: parsed.range_start,
    range_end: parsed.range_end,
  };
}

// ---------------------------------------------------------------------------
// Form → Update API payload
// ---------------------------------------------------------------------------

export function formToUpdateApi(form: MerchValueFormValues): {
  note: string | null;
  value: string | null;
  range_start: string;
  range_end: string;
} {
  const serialized: Record<string, unknown> = {
    merch_value_type: MERCH_VALUE_TYPE,
    market_zone_id: form.market_zone_id,
    commodity: form.commodity,
    crop_year: form.crop_year,
    note: form.note || null,
    value: form.value || null,
    range_start: form.range_start instanceof Date ? moment(form.range_start).format("YYYY-MM-DD") : undefined,
    range_end: form.range_end instanceof Date ? moment(form.range_end).format("YYYY-MM-DD") : undefined,
  };

  const parsed = MerchValueValidator.parse(serialized);

  return {
    note: parsed.note ?? null,
    value: parsed.value ?? null,
    range_start: parsed.range_start,
    range_end: parsed.range_end,
  };
}
